import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { MuiButton, MuiSwitch } from '@ace/ace-ui-web';
import pagesStandardStyles from '../../../../Pages.module.css';
import keyOperationalMetricsStyles from './KeyOperationalMetrics.module.css';
import { getDataGridStyles } from '../../../../GridStyles';
import {
  formatKpiAmount,
  formatLoadDate,
  isValidApiDate,
  renderEmptyValue,
  isMobileDevice,
  renderPeerGroupRankCell,
  getAlternatingRowClassName,
} from '../../../../../utils/appUtils';
import lang from '../../../../../languages/en.json';
import SkeletonDataGrid from '../../../../../components/skeletons/common/SkeletonDataGrid';
import { useBuildDynamicUrl } from '../../../../../hooks/useDynamicUrl';
import {
  selectKeyOperationalMetrics,
  selectKeyOperationalMetricsIsNull,
  selectKeyOperationalMetricsLoading,
} from '../../../../../redux/PerformanceReducer/Ecommerce/keyOperationalMetricsSlice';
import { useAppSelector } from '../../../../../redux/hooks';
import { selectIsMultiStoreEnabled } from '../../../../../redux/UserReducer/userSlice';
import type { KeyOperationalRolling2Model } from '../../../../../models/KeyOperationalRolling2Model';
import { useClickHandler } from '../../../../../utils/usageMetricsLogger';

// ─── Props interface ─────────────────────────────────────────────────────────

interface KeyOperationalMetricsProps {
  /** Pre-fetched data for print view — skips Redux read and API fetch when provided */
  data?: KeyOperationalRolling2Model | null;
  /** When true, hides interactive elements (peer group toggle, report buttons) */
  isPrintView?: boolean;
}

const PAGE_NM = 'Performance';
const TAB_NM = 'Ecommerce';
const SECTION_NM = 'Key Operational Metrics';

// Module-level constants
const MOBILE_BREAKPOINT = 850;
const MOBILE_METRIC_WIDTH = 150;
const DESKTOP_METRIC_WIDTH = 350;
const GOAL_COLUMN_WIDTH = 100;
const VALUE_MIN_WIDTH = 90;
const PEER_GROUP_MIN_WIDTH = 100;
const REPORT_COLUMN_MIN_WIDTH = 200;
const COLUMN_HEADER_HEIGHT = 52;
const COLUMN_BUFFER_PX = 1500;
const ALIGN_RIGHT = 'right' as const;
const ALIGN_LEFT = 'left' as const;

// Rounds a KPI value to 1 decimal place using string-based math to avoid a JavaScript precision bug
// where numbers like 93.05 and 22.95 round down instead of up (e.g. 93.05 → 93.0 instead of 93.1)
const roundKpiToOneDecimal = (
  value: number | string | null | undefined,
): number | null | undefined => {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return num;
  return Math.sign(num) * Number(Math.round(Number(Math.abs(num) + 'e+1')) + 'e-1');
};

// Row interface for DataGrid
interface GridRow {
  id: number;
  metric: string;
  goal: string;
  value: string;
  peerGroupRank: string;
  benchmark: string;
  reportButton?: {
    label: string;
    url: string;
  };
  kpiCode: string;
}

// Report button configuration (static data only)
interface ReportButtonConfig {
  label: string;
  urlCode: string;
  urlField: string;
}

const REPORT_BUTTON_CONFIG: Record<string, ReportButtonConfig> = {
  AH03: {
    label: lang.keyOperationalMetricsContent.reportButtons.aosItemDetail,
    urlCode: 'AH03',
    urlField: 'report',
  },
  AH08: {
    label: lang.keyOperationalMetricsContent.reportButtons.stockValidatedDetail,
    urlCode: 'AH08',
    urlField: 'report',
  },
  AH09: {
    label: lang.keyOperationalMetricsContent.reportButtons.cancelledOrderDetail,
    urlCode: 'AH09',
    urlField: 'report',
  },
  AMVS: {
    label: lang.keyOperationalMetricsContent.reportButtons.validatedTimingMatrix,
    urlCode: 'AMVS',
    urlField: 'report',
  },
  CCL1: {
    label: lang.keyOperationalMetricsContent.reportButtons.curbsideDetail,
    urlCode: 'CCL1',
    urlField: 'report',
  },
};

// Helper function to render formatted cells
const renderFormatCell = (params: GridRenderCellParams<GridRow, string>): React.ReactNode => {
  const emptyCheck = renderEmptyValue(params.value);
  if (emptyCheck) return emptyCheck;
  return params.value;
};

// renderReportButton is defined inside the component to close over handleAndLogClick

const KeyOperationalMetrics: React.FC<KeyOperationalMetricsProps> = ({
  data: dataProp,
  isPrintView = false,
}) => {
  const [showPeerGroupColumns, setShowPeerGroupColumns] = useState(false);
  const [isMobile, setIsMobile] = useState(
    isPrintView ? false : window.innerWidth <= MOBILE_BREAKPOINT,
  );

  // Check if actual mobile device for conditional report column rendering
  const isMobileDeviceCheck = isPrintView ? true : isMobileDevice();

  // Data fetch handled by Header

  // Selectors for data and loading/error states
  const reduxData = useAppSelector(selectKeyOperationalMetrics);
  const reduxLoading = useAppSelector(selectKeyOperationalMetricsLoading);
  const reduxHasError = useAppSelector(selectKeyOperationalMetricsIsNull);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  // Use prop data (print view) or Redux data (live view)
  const data = dataProp ?? reduxData;
  const isLoading = isPrintView ? false : reduxLoading;
  const hasError = isPrintView ? false : reduxHasError;

  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);

  // Render report button inside component to close over handleAndLogClick
  const renderReportButton = useCallback(
    (params: GridRenderCellParams<GridRow>): JSX.Element => {
      const reportButton = params.row.reportButton;

      if (!reportButton) {
        return <div className={pagesStandardStyles.centeredDash}>-</div>;
      }

      return (
        <MuiButton
          colors="secondary"
          iconPosition="right"
          iconType="launch"
          size="small"
          onClick={() => {
            handleAndLogClick(`${SECTION_NM} - ${reportButton.label}`, {
              url: reportButton.url,
              urlTarget: '_blank',
            });
          }}>
          {reportButton.label}
        </MuiButton>
      );
    },
    [handleAndLogClick],
  );

  // Build report button map with dynamic URLs
  const reportButtonMap = useMemo(() => {
    const map: Record<string, { label: string; url: string }> = {};
    Object.entries(REPORT_BUTTON_CONFIG).forEach(([kpiCode, config]) => {
      const url = buildDynamicUrl('WFC', config.urlCode, config.urlField);
      if (url) {
        map[kpiCode] = {
          label: config.label,
          url: url,
        };
      }
    });
    return map;
  }, [buildDynamicUrl]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Create rows from API data (handles null data gracefully)
  const rowData = useMemo(() => {
    if (!data?.kpis) return [];
    const kpis = data.kpis;
    return kpis.map((kpi, index) => ({
      id: index + 1,
      metric: kpi.description,
      goal: formatKpiAmount(
        kpi.kpiGoal,
        kpi.kpiFormat,
        kpi.symbol,
        Number(kpi.kpiGoal) % 1 !== 0 ? 1 : 0,
      ),
      value: formatKpiAmount(roundKpiToOneDecimal(kpi.currentKpiAmount), kpi.kpiFormat, null, 1),
      peerGroupRank: kpi.peerGroupRank,
      benchmark: formatKpiAmount(kpi.benchmark, kpi.kpiFormat, kpi.symbol),
      reportButton: reportButtonMap[kpi.kpiCode],
      kpiCode: kpi.kpiCode,
    }));
  }, [data, reportButtonMap]);

  // Define columns
  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'metric',
        headerName: lang.pageContent.metric,
        width: isMobile ? MOBILE_METRIC_WIDTH : DESKTOP_METRIC_WIDTH,
        headerClassName: pagesStandardStyles.columnHeader,
        cellClassName: pagesStandardStyles.stickyColumn,
        renderCell: params => {
          const text = params.value as string;
          // Check if text contains parentheses
          const parenMatch = text.match(/^([^(]+)(\(.+\))$/);
          if (parenMatch) {
            return (
              <span>
                {parenMatch[1]}
                <span className={keyOperationalMetricsStyles.lightWeight}>{parenMatch[2]}</span>
              </span>
            );
          }
          return text;
        },
      },
      {
        field: 'goal',
        headerName: lang.dataGrid.columns.goal,
        minWidth: GOAL_COLUMN_WIDTH,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: ALIGN_RIGHT,
        renderCell: renderFormatCell,
      },
      {
        field: 'value',
        headerName: lang.keyOperationalMetricsContent.columnValue,
        minWidth: VALUE_MIN_WIDTH,
        flex: 1,
        headerClassName: pagesStandardStyles.columnHeader,
        align: ALIGN_RIGHT,
        renderCell: renderFormatCell,
      },
      ...(showPeerGroupColumns
        ? ([
            {
              field: 'peerGroupRank',
              headerName: isMultiStoreEnabled
                ? lang.pageContent.msGroupRank
                : lang.pageContent.peerGroupRank,
              minWidth: PEER_GROUP_MIN_WIDTH,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: ALIGN_RIGHT,
              renderCell: renderPeerGroupRankCell,
            },
            {
              field: 'benchmark',
              headerName: lang.pageContent.percentBenchmark,
              minWidth: PEER_GROUP_MIN_WIDTH,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: ALIGN_RIGHT,
              renderCell: renderFormatCell,
            },
          ] as GridColDef[])
        : []),
      ...(!isMobileDeviceCheck
        ? ([
            {
              field: 'reportButton',
              headerName: lang.keyOperationalMetricsContent.columnReports,
              minWidth: REPORT_COLUMN_MIN_WIDTH,
              flex: 1,
              headerClassName: pagesStandardStyles.columnHeader,
              align: ALIGN_LEFT,
              renderCell: renderReportButton,
            },
          ] as GridColDef[])
        : []),
    ];

    return baseColumns;
  }, [showPeerGroupColumns, isMobile, isMobileDeviceCheck, renderReportButton, isMultiStoreEnabled]);

  // Priority order: Loading -> Show Data (error/no-data handled inline)

  // 1. Show skeleton while loading
  if (isLoading) {
    if (isPrintView) return null;
    return <SkeletonDataGrid />;
  }

  // 2. Show error message if API call failed (data is null)
  if (hasError || !data) {
    if (isPrintView) return null;
    const errorMessage = lang.pageContent.dataUnavailableError.replace(
      '{section}',
      lang.keyOperationalMetricsContent.title,
    );
    return (
      <div className={pagesStandardStyles.errorContainer}>
        <p>{errorMessage}</p>
      </div>
    );
  }

  // 3. Show "no data" message if API succeeded but returned empty KPIs
  if (rowData.length === 0) {
    if (isPrintView) return null;
    return (
      <div className={pagesStandardStyles.errorContainer}>
        <p>{lang.pageContent.noData}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className={pagesStandardStyles.pageHeader}>
        <div className={pagesStandardStyles.headerContainer}>
          <div>
            <h2 className={pagesStandardStyles.pageTitle}>
              {lang.keyOperationalMetricsContent.title}{' '}
              <span className={pagesStandardStyles.pageSubtitle}>
                {lang.keyOperationalMetricsContent.rollingWeeks}
              </span>
            </h2>
            <p className={pagesStandardStyles.pageSubtitle}>
              {lang.pageContent.datePrefix}{' '}
              {isValidApiDate(data?.asOfDate) ? formatLoadDate(data.asOfDate) : 'N/A'}
            </p>
          </div>
          {!isPrintView && (
            <div className={pagesStandardStyles.toggleContainer}>
              <MuiSwitch
                key={showPeerGroupColumns.toString()}
                color="primary"
                defaultChecked={showPeerGroupColumns}
                label={lang.pageContent.toggleShowPeerGroup}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setShowPeerGroupColumns(event.target.checked);
                }}
                size="medium"
              />
            </div>
          )}
        </div>
      </div>

      {/* DataGrid */}
      <div className={keyOperationalMetricsStyles.keyOperationalMetricsGrid}>
        {hasError || !data ? (
          <div className={pagesStandardStyles.errorContent}>
            <p>
              {lang.pageContent.dataUnavailableError.replace(
                '{section}',
                lang.keyOperationalMetricsContent.title,
              )}
            </p>
          </div>
        ) : rowData.length === 0 ? (
          <div className={pagesStandardStyles.errorContent}>
            <p>{lang.pageContent.noData}</p>
          </div>
        ) : (
          <DataGrid
            key={`datagrid-${showPeerGroupColumns}`}
            rows={rowData}
            columns={columns}
            disableRowSelectionOnClick
            disableVirtualization
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            hideFooter
            autoHeight
            className={pagesStandardStyles.dataGrid}
            getRowClassName={params =>
              getAlternatingRowClassName(
                params,
                pagesStandardStyles.evenRow,
                pagesStandardStyles.oddRow,
              )
            }
            columnHeaderHeight={COLUMN_HEADER_HEIGHT}
            columnBufferPx={COLUMN_BUFFER_PX}
            getRowHeight={() => (isMobile ? 'auto' : null)}
            sx={{
              [`@media (max-width: ${MOBILE_BREAKPOINT}px)`]: {
                '& .MuiDataGrid-virtualScroller .MuiDataGrid-row .MuiDataGrid-cell': {
                  maxHeight: 'none',
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                },
              },
              ...getDataGridStyles('metric'),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default KeyOperationalMetrics;
